"use client";
import React, { useState, useEffect, useRef } from "react";
import { Input } from "@/components/atoms/Input";
import { cn } from "@/lib/utils";
import { resendOtp } from "@/api/lead";
import { LEAD_DROP_STEPS } from "@/constants/lead";
import { useIsMobile } from "@/hooks/useIsMobile";

interface OtpScreenProps {
  setStep: (step: string) => void;
  phoneNumber: string;
  isExistingLeadId: string;
  otp: string[];
  setOtp: (otp: string[]) => void;
  error?: string;
  otpTitleText?: string;
  otpTitleColor?: string;
  editTextColor?: string;
  resendTextColor?: string;
  /** IIT lead modal OTP: title in navy (mobile) or parent column (desktop); pills + copy per Figma 243:6788 / 255:13275. */
  figmaIitLeadModal?: boolean;
  /** Enrollments desktop card: 20px semibold title + coerce default black CMS color to accent (matches Leaddrop7 IIT desktop OTP). */
  embeddedCard?: boolean;
}

/** Coerce CMS black / empty otp title colors to IIT accent (#DB5803). */
function resolveEmbeddedOtpTitleColor(raw?: string | null): string {
  const t = String(raw ?? "").trim().toLowerCase().replace(/\s/g, "");
  const blackTokens = new Set([
    "",
    "#000",
    "#000000",
    "black",
    "rgb(0,0,0)",
    "rgba(0,0,0,1)",
  ]);
  if (blackTokens.has(t)) return "#DB5803";
  const orig = String(raw ?? "").trim();
  return orig || "#DB5803";
}

const OtpScreen: React.FC<OtpScreenProps> = ({
  setStep,
  phoneNumber,
  isExistingLeadId,
  otp,
  setOtp,
  error,
  otpTitleText,
  otpTitleColor,
  editTextColor,
  resendTextColor,
  figmaIitLeadModal = false,
  embeddedCard = false,
}) => {
  const isMobile = useIsMobile(800);
  const [countdown, setCountdown] = useState<number>(30);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    // Start countdown timer
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleChange = (index: number, value: string) => {
    // Only allow single digit
    if (value.length > 1) return;

    // Only allow numbers
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 4);
    if (/^\d{4}$/.test(pastedData)) {
      const newOtp = pastedData.split("");
      setOtp(newOtp);
      inputRefs.current[3]?.focus();
    }
  };

  const handleResend = async () => {
    if (countdown === 0) {
      setCountdown(30);
      setOtp(["", "", "", ""]);
      inputRefs.current[0]?.focus();
      await resendOtp({
        lead_id: isExistingLeadId,
        phoneNumber: phoneNumber,
      });
    }
  };

  const accent = "#DB5803";
  const fm =
    embeddedCard || figmaIitLeadModal
      ? accent
      : editTextColor?.trim() || accent;
  const rm =
    embeddedCard || figmaIitLeadModal
      ? accent
      : resendTextColor?.trim() || accent;

  return (
    <div
      className={cn(
        "flex w-full min-w-0 flex-col",
        figmaIitLeadModal
          ? "gap-8 max-lg:items-center lg:items-center lg:gap-8"
          : "sm:gap-5 md:gap-5 lg:gap-5",
      )}
    >
      <div
        className={cn(
          "flex flex-col text-center",
          figmaIitLeadModal && "hidden",
          !figmaIitLeadModal && "sm:gap-0 sm:block sm:text-center",
          !figmaIitLeadModal &&
            !embeddedCard && {
              "xs:absolute xs:top-[4%] xs:left-1/2 xs:transform xs:-translate-x-1/2":
                isMobile,
            },
          figmaIitLeadModal && "hidden",
          !figmaIitLeadModal && "sm:gap-0 sm:block sm:text-center",
          !figmaIitLeadModal &&
            !embeddedCard && {
              "xs:absolute xs:top-[4%] xs:left-1/2 xs:transform xs:-translate-x-1/2":
                isMobile,
            },
        )}
      >
        <div
          className={cn(
            embeddedCard &&
              "font-['Poppins',sans-serif] text-[20px] font-semibold leading-[138%]",
            !embeddedCard &&
              !figmaIitLeadModal &&
              "xs:text-heading-6-semibold sm:text-heading-3-semibold sm:leading-normal md:text-heading-3-semibold md:leading-normal xl:text-heading-3-semibold xl:leading-normal",
          )}
          style={{
            color: embeddedCard
              ? resolveEmbeddedOtpTitleColor(otpTitleColor)
              : otpTitleColor || "#000000",
          }}
        >
          {otpTitleText || "Enter OTP"}
        </div>
      </div>

      <div
        className={cn(
          "flex min-w-0 flex-col items-center",
          figmaIitLeadModal
            ? "w-full gap-8 max-lg:w-full lg:items-center lg:gap-8"
            : "xs:gap-3 sm:gap-4",
        )}
      >
        <p
          className={cn(
            "text-center",
            figmaIitLeadModal
              ? "font-['Poppins',sans-serif] text-[14px] leading-6 tracking-[-0.0747px] text-[#171719] max-lg:whitespace-nowrap lg:w-full lg:text-center lg:text-[16px] lg:tracking-[-0.0853px] lg:whitespace-normal"
              : embeddedCard
                ? "flex flex-wrap items-center justify-center gap-1 font-['Poppins',sans-serif] text-[16px] font-normal leading-6 text-[#171719]"
                : "flex flex-wrap items-center justify-center gap-1 sm:text-heading-8-medium",
          )}
        >
          <span>
            OTP sent to +91 {phoneNumber}{" "}
          </span>
          <button
            type="button"
            className={cn(
              "cursor-pointer font-medium underline decoration-solid [text-decoration-skip-ink:none]",
              embeddedCard &&
                "font-['Poppins',sans-serif] text-[16px] leading-6",
              figmaIitLeadModal &&
                "font-['Poppins',sans-serif] text-[14px] leading-[1.3] lg:text-[16px]",
            )}
            style={{ color: fm }}
            onClick={() => setStep(LEAD_DROP_STEPS.MOBILE_NUMBER)}
          >
            Edit
          </button>
        </p>

        <div
          className={cn(
            "flex flex-col items-center",
            figmaIitLeadModal ? "gap-8" : "gap-3",
          )}
        >
          <div
            className={cn(
              "flex justify-center",
              figmaIitLeadModal
                ? "gap-4 justify-center"
                : embeddedCard
                  ? "gap-4 justify-center"
                  : "gap-3",
            )}
          >
            {otp.map((value, index) => (
              <Input
                key={index}
                ref={(el) => {
                  inputRefs.current[index] = el;
                }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={value}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                className={cn(
                  "text-center font-semibold outline-none transition-colors",
                  "focus:outline-none focus:ring-0",
                  figmaIitLeadModal &&
                    cn(
                      "size-[50px] rounded-[10px] border-2 border-[#291973] bg-[#E5E5F9] p-0 text-[14px] leading-6 text-[#171719] shadow-none [font-variant-numeric:tabular-nums]",
                      "focus:border-[#291973]",
                    ),
                  embeddedCard &&
                    !figmaIitLeadModal &&
                    cn(
                      "size-[58px] rounded-[10px] border border-[#291973] bg-[#EEEBF7] p-0 font-['Poppins',sans-serif] text-[24px] leading-none text-[#171719] shadow-none [font-variant-numeric:tabular-nums]",
                      "focus:border-[#291973]",
                      error
                        ? "border-red-500 focus:border-red-500"
                        : undefined,
                    ),
                  !figmaIitLeadModal &&
                    !embeddedCard && [
                      "h-10 w-10 rounded-lg border-2 text-lg",
                      error
                        ? "border-red-500 focus:border-red-500"
                        : "border-gray-3 focus:border-black",
                    ],
                  figmaIitLeadModal &&
                    error &&
                    "border-[#FF0D00] bg-[#FF0D0033] focus:border-[#FF0D00]",
                )}
              />
            ))}
          </div>
          {error &&
            (figmaIitLeadModal ? (
              <div className="flex h-fit w-fit max-w-[310px] items-center justify-center gap-[10px] rounded-[10px] bg-[#FF7C751A] px-[10px] py-[10px] md:px-[20px]">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-auto shrink-0"
                  aria-hidden
                >
                  <path
                    d="M21.76 15.92L15.36 4.4C14.5 2.85 13.31 2 12 2C10.69 2 9.49998 2.85 8.63998 4.4L2.23998 15.92C1.42998 17.39 1.33998 18.8 1.98998 19.91C2.63998 21.02 3.91998 21.63 5.59998 21.63H18.4C20.08 21.63 21.36 21.02 22.01 19.91C22.66 18.8 22.57 17.38 21.76 15.92ZM11.25 9C11.25 8.59 11.59 8.25 12 8.25C12.41 8.25 12.75 8.59 12.75 9V14C12.75 14.41 12.41 14.75 12 14.75C11.59 14.75 11.25 14.41 11.25 14V9ZM12.71 17.71C12.66 17.75 12.61 17.79 12.56 17.83C12.5 17.87 12.44 17.9 12.38 17.92C12.32 17.95 12.26 17.97 12.19 17.98C12.13 17.99 12.06 18 12 18C11.94 18 11.87 17.99 11.8 17.98C11.74 17.97 11.68 17.95 11.62 17.92C11.56 17.9 11.5 17.87 11.44 17.83C11.39 17.79 11.34 17.75 11.29 17.71C11.11 17.52 11 17.26 11 17C11 16.74 11.11 16.48 11.29 16.29C11.34 16.25 11.39 16.21 11.44 16.17C11.5 16.13 11.56 16.1 11.62 16.08C11.68 16.05 11.74 16.03 11.8 16.02C11.93 15.99 12.07 15.99 12.19 16.02C12.26 16.03 12.32 16.05 12.38 16.08C12.44 16.1 12.5 16.13 12.56 16.17C12.61 16.21 12.66 16.25 12.71 16.29C12.89 16.48 13 16.74 13 17C13 17.26 12.89 17.52 12.71 17.71Z"
                    fill="#FB1206"
                  />
                </svg>
                <span className="text-[16px] font-medium leading-[28px] text-[#FB1206]">
                  {error}
                </span>
              </div>
            ) : (
              <p className="mt-1 max-w-full text-center text-sm text-red-500">
                {error}
              </p>
            ))}
        </div>

        {figmaIitLeadModal ? (
          countdown > 0 ? (
            <div className="flex items-center justify-center gap-[4px] py-[8px] font-['Poppins',sans-serif] text-[16px] font-medium leading-[24px]">
              <span className="text-[#DB580366] underline">Resend OTP </span>|
              <span className="text-black">{countdown} Sec</span>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleResend}
              className="flex w-full items-center justify-center font-['Poppins',sans-serif] text-base font-medium text-[#DB5803] underline"
            >
              Resend OTP
            </button>
          )
        ) : (
          <div
            className={cn(
              "flex items-center justify-center gap-2 text-sm",
              !embeddedCard && "text-secondary-main",
              embeddedCard &&
                "font-['Poppins',sans-serif] text-[16px] font-normal leading-6 text-[#DB5803]",
            )}
          >
            {countdown > 0 ? (
              <span>
                Resend in <span>{countdown}</span> Sec
              </span>
            ) : (
              <button
                type="button"
                onClick={handleResend}
                className="cursor-pointer font-semibold underline decoration-solid"
                style={{ color: rm }}
              >
                Resend OTP
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OtpScreen;
